/**
 * Wishlist Manager - Astrea Beauty
 * LocalStorage based - persists across sessions and after login/logout
 */

const WISHLIST_KEY = 'astreabeauty_wishlist';

class WishlistManager {
  constructor() {
    this.items = this._load();
    this._bindEvents();
    this._initOnReady();
  }

  _load() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      // Migrate old string IDs to objects, filtering out invalid items
      return parsed.map(item => {
        if (typeof item === 'object' && item !== null && item.id) {
          return item;
        } else if (typeof item === 'string' || typeof item === 'number') {
          return {
            id: String(item),
            title: 'Product',
            handle: '',
            image: '',
            price: '',
            comparePrice: ''
          };
        }
        return null;
      }).filter(Boolean);
    } catch {
      return [];
    }
  }

  _save() {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(this.items));
    } catch {}
  }

  _dispatch() {
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: { items: this.items, count: this.items.length }
      })
    );
  }

  _initOnReady() {
    const run = () => {
      this._updateAllButtons();
      this._updateCounters();
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  _bindEvents() {
    document.addEventListener('wishlist:updated', () => {
      this._updateAllButtons();
      this._updateCounters();
    });

    // Re-populate wishlist count and buttons if header/page elements are replaced during cart AJAX updates
    document.addEventListener('cart:update', () => {
      setTimeout(() => {
        this._updateAllButtons();
        this._updateCounters();
      }, 200); // 200ms delay to ensure theme's DOM replacement is complete
    });

    // Button click delegation
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const productId = btn.dataset.productId;
      const productTitle = btn.dataset.productTitle || 'Product';
      if (!productId) return;

      const product = {
        id: String(productId),
        title: productTitle,
        handle: btn.dataset.productHandle || '',
        image: btn.dataset.productImage || '',
        price: btn.dataset.productPrice || '',
        comparePrice: btn.dataset.productComparePrice || ''
      };

      const wasAdded = this.toggle(product);
      this._animateBtn(btn);
      this._showToast(wasAdded, productTitle);
    });
  }

  has(productId) {
    return this.items.some(item => String(item.id) === String(productId));
  }

  add(product) {
    const id = String(product.id);
    if (!this.has(id)) {
      this.items.push(product);
      this._save();
      this._dispatch();
      return true;
    }
    return false;
  }

  remove(productId) {
    const id = String(productId);
    const initialLength = this.items.length;
    this.items = this.items.filter(item => String(item.id) !== id);
    if (this.items.length !== initialLength) {
      this._save();
      this._dispatch();
      return true;
    }
    return false;
  }

  toggle(product) {
    if (this.has(product.id)) {
      this.remove(product.id);
      return false;
    } else {
      this.add(product);
      return true;
    }
  }

  getAll() {
    return [...this.items];
  }

  count() {
    return this.items.length;
  }

  _updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      const productId = String(btn.dataset.productId || '');
      if (!productId) return;

      const active = this.has(productId);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-pressed', String(active));

      const icon = btn.querySelector('.wishlist-icon');
      if (icon) icon.classList.toggle('wishlist-icon--filled', active);

      const label = btn.querySelector('.wishlist-btn__label');
      if (label) {
        label.textContent = active ? 'Wishlisted' : 'Add to Wishlist';
      }

      const tooltip = btn.querySelector('.wishlist-btn__tooltip');
      if (tooltip) {
        tooltip.textContent = active ? 'Remove from Wishlist' : 'Add to Wishlist';
      }
    });
  }

  _updateCounters() {
    const count = this.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  _animateBtn(btn) {
    btn.classList.add('wishlist-btn--pop');
    setTimeout(() => btn.classList.remove('wishlist-btn--pop'), 500);
  }

  _showToast(added, productTitle) {
    let toast = document.getElementById('wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-toast';
      toast.className = 'wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    if (added) {
      toast.innerHTML = `
        <svg class="wishlist-toast__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
          <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z" fill="currentColor"/>
        </svg>
        <span>Added to Wishlist &mdash; <a href="/pages/wishlist" class="wishlist-toast__link">View</a></span>`;
    } else {
      toast.innerHTML = `
        <span class="wishlist-toast__icon wishlist-toast__icon--remove">&times;</span>
        <span>Removed from Wishlist</span>`;
    }

    toast.classList.remove('wishlist-toast--out');
    toast.classList.add('wishlist-toast--visible');

    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.add('wishlist-toast--out');
      setTimeout(() => toast.classList.remove('wishlist-toast--visible', 'wishlist-toast--out'), 400);
    }, 3000);
  }
}

window.wishlist = new WishlistManager();

/* ── Wishlist Page Custom Element ── */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._render();
    document.addEventListener('wishlist:updated', () => this._render());
  }

  async _render() {
    const items = window.wishlist.getAll();
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const countEl = this.querySelector('[data-wishlist-page-count]');

    if (countEl) countEl.textContent = items.length;

    if (items.length === 0) {
      if (grid) grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    if (grid) {
      // Check if we have items that need one-time details fetching (e.g. old ID strings or migrated empty ones)
      const needsFetching = items.some(item => !item.handle);

      if (needsFetching) {
        grid.innerHTML = '<div class="wishlist-page__loading"><span class="wishlist-loading-dot"></span></div>';

        const resolvedItems = await Promise.all(
          items.map(async (item) => {
            if (item.handle) return item;
            try {
              const res = await fetch(`/search/suggest.json?q=id:${item.id}&resources[type]=product&resources[limit]=1`);
              const data = await res.json();
              const p = data?.resources?.results?.products?.[0];
              if (p) {
                return {
                  id: String(p.id),
                  title: p.title,
                  handle: p.handle,
                  image: p.featured_image?.url ? `${p.featured_image.url}&width=600` : '',
                  price: p.price || '',
                  comparePrice: p.compare_at_price_min || ''
                };
              }
            } catch (err) {}
            return null;
          })
        );

        const validItems = resolvedItems.filter(Boolean);
        // Save back to localStorage so we don't have to fetch them again
        window.wishlist.items = validItems;
        window.wishlist._save();

        if (countEl) countEl.textContent = validItems.length;

        if (validItems.length === 0) {
          grid.innerHTML = '';
          if (empty) empty.hidden = false;
          return;
        }

        grid.innerHTML = validItems.map((p) => this._cardHTML(p)).join('');
      } else {
        grid.innerHTML = items.map((p) => this._cardHTML(p)).join('');
      }

      window.wishlist._updateAllButtons();
    }
  }

  _cardHTML(p) {
    const imgSrc = p.image;
    const price = p.price ? this._money(p.price) : '';
    const comparePrice =
      p.comparePrice && parseFloat(p.comparePrice) > parseFloat(p.price)
        ? this._money(p.comparePrice)
        : null;

    return `
      <div class="wishlist-page__card">
        <a href="/products/${p.handle}" class="wishlist-page__card-img-wrap">
          ${imgSrc ? `<img src="${imgSrc}" alt="${p.title}" class="wishlist-page__card-img" loading="lazy">` : '<div class="wishlist-page__card-img wishlist-page__card-img--placeholder"></div>'}
          <button
            class="wishlist-page__remove-btn wishlist-btn wishlist-btn--overlay is-wishlisted"
            data-wishlist-btn
            data-product-id="${p.id}"
            data-product-title="${p.title}"
            aria-label="Remove from wishlist"
            aria-pressed="true"
          >
            <span class="wishlist-icon wishlist-icon--filled">${HEART_SVG}</span>
            <span class="wishlist-btn__tooltip">Remove from Wishlist</span>
          </button>
        </a>
        <div class="wishlist-page__card-body">
          <a href="/products/${p.handle}" class="wishlist-page__card-title">${p.title}</a>
          <div class="wishlist-page__card-price">
            <span class="wishlist-page__price${comparePrice ? ' wishlist-page__price--sale' : ''}">${price}</span>
            ${comparePrice ? `<span class="wishlist-page__price-compare">${comparePrice}</span>` : ''}
          </div>
        </div>
      </div>`;
  }

  _money(val) {
    if (!val) return '';
    let amount = parseFloat(val);
    if (isNaN(amount)) return '';
    if (String(val).includes('.')) {
      // Already in major currency units, e.g. "48.00"
    } else {
      // In cents, e.g. 4800
      amount = amount / 100;
    }
    
    // Dynamically detect currency symbol, default to USD ($)
    const currency = window.Shopify?.currency?.active || 'USD';
    let symbol = '$';
    if (currency === 'INR') symbol = '₹';
    else if (currency === 'EUR') symbol = '€';
    else if (currency === 'GBP') symbol = '£';
    else if (currency === 'CAD' || currency === 'AUD') symbol = '$';
    
    return `${symbol}${amount.toFixed(2)}`;
  }
}

const HEART_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
  <path class="wishlist-icon__outline" d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z"/>
</svg>`;

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
